import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, SearchIcon, CloseIcon, TrashIcon, DownloadIcon } from './Icons';
import Pagination from './Pagination';
import { downloadCsv } from './csvExport';
import { localDateStr } from './callUtils';

const DEFAULT_PAGE_SIZE = 50;
// Same silent-background-refresh pattern as InternationalOrders.js — more
// than one agent can be logging a payout here, so poll rather than requiring
// a manual refresh to see what a teammate just logged.
const AUTO_REFRESH_MS = 10000;
// Mirrors BACKEND_PAYMENT_MAX_IMAGE_BYTES in server/ivr/routes.js — checked
// here too so an oversized file is rejected instantly.
const MAX_PROOF_IMAGE_BYTES = 25 * 1024 * 1024;

function formatDateTime(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatAmount(amount) {
  if (!amount) return '-';
  const value = Number(amount);
  return Number.isNaN(value) ? amount : value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function formatQty(qty) {
  const value = Number(qty);
  return Number.isFinite(value) ? value.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : qty;
}

// Products on a row: typed in the form, or copied from an international
// order's lines when its payment was recorded automatically.
function ProductsCell({ products }) {
  if (!Array.isArray(products) || products.length === 0) return '-';
  return (
    <ul className="backend-payment-products">
      {products.map((product, index) => (
        <li key={`${product.name}-${index}`}>
          {product.name} <strong>× {formatQty(product.qty)}</strong>
        </li>
      ))}
    </ul>
  );
}

const emptyProductRow = () => ({ name: '', qty: '1' });

// "Log Payment" form — a plain agent-entered record with no ERP/Razorpay
// source (see server/ivr/db.js's ivr_backend_payments), for a manual payout
// made to a customer (refund/replacement/compensation) outside those
// systems. The proof image (if any) is uploaded first, same two-step
// upload-then-submit flow as InternationalOrders.js's bill image.
function AddBackendPaymentModal({ onClose, onSubmit }) {
  const { authFetch } = useAuth();
  const [orderNumber, setOrderNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState('');
  const [reason, setReason] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [products, setProducts] = useState([emptyProductRow()]);
  const [proofImage, setProofImage] = useState(null); // File
  const [proofImagePreviewUrl, setProofImagePreviewUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Live object URL for the picked-but-not-yet-uploaded image, so "View"
  // works instantly — revoked whenever the picked file changes or unmounts.
  useEffect(() => {
    if (!proofImage) {
      setProofImagePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(proofImage);
    setProofImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [proofImage]);

  const handleImageChange = (event) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = ''; // allow re-picking the same file after removing it
    if (!picked) return;
    if (!picked.type.startsWith('image/')) {
      setImageError('Proof must be an image file');
      return;
    }
    if (picked.size > MAX_PROOF_IMAGE_BYTES) {
      setImageError('File is too large — the limit is 25 MB');
      return;
    }
    setImageError('');
    setProofImage(picked);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!orderNumber.trim()) {
      setError('Order number is required.');
      return;
    }
    if (!amount.trim()) {
      setError('Amount is required.');
      return;
    }
    if (!mobileNumber.trim()) {
      setError('Mobile number is required.');
      return;
    }
    if (imageError) {
      setError(imageError);
      return;
    }
    const filledProducts = products.filter((product) => product.name.trim());
    if (filledProducts.some((product) => !(Number(product.qty) > 0))) {
      setError('Enter a quantity above 0 for each product.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let proofImageUrl;
      if (proofImage) {
        const formData = new FormData();
        formData.append('file', proofImage);
        const uploadResponse = await authFetch('/api/ivr/backend-payments/attachment', {
          method: 'POST',
          body: formData,
        });
        const uploadBody = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadBody?.error || 'Proof image upload failed');
        proofImageUrl = uploadBody.url;
      }
      await onSubmit({
        orderNumber: orderNumber.trim(),
        amount: amount.trim(),
        account: account.trim(),
        reason: reason.trim(),
        mobileNumber: mobileNumber.trim(),
        products: filledProducts.map((product) => ({ name: product.name.trim(), qty: Number(product.qty) })),
        proofImageUrl,
      });
    } catch (err) {
      setError(err.message || 'Failed to save this payment.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel modal-panel-narrow">
        <div className="modal-header">
          <div>
            <h2>Log Backend Payment</h2>
            <p className="modal-subtitle">Logged locally — not tied to any ERP or Razorpay record.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Order Number</span>
            <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} autoFocus required />
          </label>
          <label className="login-field">
            <span>Amount</span>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </label>
          <label className="login-field">
            <span>Account</span>
            <input
              type="text"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="Which account the money was paid from"
            />
          </label>
          <label className="login-field">
            <span>Reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this payment was made"
              rows={3}
            />
          </label>
          <label className="login-field">
            <span>Mobile Number</span>
            <input type="text" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} required />
          </label>
          <div className="login-field">
            <span>Products (optional)</span>
            {products.map((product, index) => (
              <div className="backend-payment-product-row" key={index}>
                <input
                  type="text"
                  value={product.name}
                  placeholder="Product name"
                  aria-label={`Product ${index + 1} name`}
                  onChange={(e) =>
                    setProducts((prev) => prev.map((row, i) => (i === index ? { ...row, name: e.target.value } : row)))
                  }
                />
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={product.qty}
                  placeholder="Qty"
                  aria-label={`Product ${index + 1} quantity`}
                  onChange={(e) =>
                    setProducts((prev) => prev.map((row, i) => (i === index ? { ...row, qty: e.target.value } : row)))
                  }
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  aria-label={`Remove product ${index + 1}`}
                  onClick={() =>
                    setProducts((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [emptyProductRow()]))
                  }
                >
                  <CloseIcon width={12} height={12} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-small backend-payment-add-product"
              onClick={() => setProducts((prev) => [...prev, emptyProductRow()])}
            >
              + Add product
            </button>
          </div>
          <label className="login-field">
            <span>Proof Image (optional)</span>
            <input type="file" accept="image/*" onChange={handleImageChange} />
          </label>
          {proofImage && (
            <div className="order-query-attachment-preview">
              <span>{proofImage.name}</span>
              <span className="order-query-attachment-preview-actions">
                {proofImagePreviewUrl && (
                  <a href={proofImagePreviewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
                    View
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    setProofImage(null);
                    setImageError('');
                  }}
                >
                  Remove
                </button>
              </span>
            </div>
          )}
          {imageError && <div className="login-error">{imageError}</div>}
          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (proofImage ? 'Uploading…' : 'Saving…') : 'Log Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Renders the proof-image thumbnail/link for one row. onError falls back to
// a plain "View" link so a broken/missing image never blocks the row.
function ProofCell({ url }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (!url) return '-';
  if (imgFailed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        View
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img src={url} alt="Proof" className="international-order-bill-thumb" onError={() => setImgFailed(true)} />
    </a>
  );
}

// Backend Payments — a flat add-and-list log of manual payouts made to
// customers outside Razorpay/Shopify, no ERP/Razorpay source and no status
// workflow. Fed entirely by agents via the Log Payment form above;
// server/ivr/db.js's ivr_backend_payments is the only source of truth.
function BackendPayments() {
  const { authFetch } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPayments = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/backend-payments');
      const body = await response.json();
      if (!response.ok) {
        if (!silent) setPayments([]);
        setError(body?.error || 'Failed to fetch backend payments.');
        return;
      }
      setError('');
      setPayments(Array.isArray(body?.payments) ? body.payments : []);
    } catch (err) {
      if (!silent) setPayments([]);
      setError(err.message || 'Failed to fetch backend payments.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    const interval = setInterval(() => fetchPayments({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const filteredPayments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return payments;
    return payments.filter((payment) =>
      [
        payment.order_number,
        payment.account,
        payment.reason,
        payment.mobile_number,
        ...(Array.isArray(payment.products) ? payment.products.map((product) => product.name) : []),
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [payments, search]);

  useEffect(() => {
    setPage(1);
  }, [search, payments.length]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = filteredPayments.slice((page - 1) * pageSize, page * pageSize);

  // Sr No — a stable serial number, oldest payment = 1, built from the full
  // unfiltered `payments` list so it never reshuffles under a search or when
  // paging, same convention as InternationalOrders.js. Ordered by date, then
  // id: automatic international/legacy rows carry the payment's or order's
  // own date, which can be older than rows added before them.
  const serialNumberByPaymentId = useMemo(() => {
    const map = new Map();
    [...payments]
      .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || a.id - b.id)
      .forEach((payment, index) => map.set(payment.id, index + 1));
    return map;
  }, [payments]);

  // Exports every row matching the current search (all pages, not just the
  // one on screen). Products go in one cell as "Name x qty; Name x qty"
  // (a plain x: downloadCsv writes no BOM, so Excel would garble "×").
  const handleExportCsv = () => {
    const headers = [
      'Sr No', 'Order Number', 'Amount', 'Products', 'Account', 'Reason',
      'Mobile Number', 'Proof Image', 'Logged By', 'Logged On',
    ];
    const rows = filteredPayments.map((payment) => [
      serialNumberByPaymentId.get(payment.id),
      payment.order_number || '',
      payment.amount || '',
      (Array.isArray(payment.products) ? payment.products : [])
        .map((product) => `${product.name} x ${formatQty(product.qty)}`)
        .join('; '),
      payment.account || '',
      payment.reason || '',
      payment.mobile_number || '',
      payment.proof_image_url || '',
      payment.agent || '',
      payment.created_at ? formatDateTime(payment.created_at) : '',
    ]);
    downloadCsv(`backend-payments-${localDateStr(new Date())}.csv`, headers, rows);
  };

  const handleAdd = async (fields) => {
    const response = await authFetch('/api/ivr/backend-payments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to save this payment.');
    setPayments((prev) => [body, ...prev]);
    setShowAddForm(false);
  };

  const handleDelete = async (payment) => {
    if (!window.confirm(`Delete the backend payment logged for order ${payment.order_number || payment.mobile_number}?`)) return;
    setDeletingId(payment.id);
    try {
      const response = await authFetch(`/api/ivr/backend-payments/${payment.id}`, { method: 'DELETE' });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Failed to delete this payment.');
      setPayments((prev) => prev.filter((p) => p.id !== payment.id));
    } catch (err) {
      setError(err.message || 'Failed to delete this payment.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchPayments()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by order number, product, account, reason, or mobile number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search backend payments"
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary export-csv-btn"
          onClick={handleExportCsv}
          disabled={loading || filteredPayments.length === 0}
        >
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setShowAddForm(true)}>
          Log Payment
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Order Number</th>
                <th>Amount</th>
                <th>Products</th>
                <th>Account</th>
                <th>Reason</th>
                <th>Mobile Number</th>
                <th>Proof</th>
                <th>Logged By</th>
                <th>Logged On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load backend payments.'
                      : search
                      ? 'No backend payments match this search.'
                      : 'No backend payments logged yet.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((payment) => (
                  <tr key={payment.id}>
                    <td>{serialNumberByPaymentId.get(payment.id)}</td>
                    <td>{payment.order_number || '-'}</td>
                    <td>{formatAmount(payment.amount)}</td>
                    <td>
                      <ProductsCell products={payment.products} />
                    </td>
                    <td>{payment.account || '-'}</td>
                    <td>{payment.reason || '-'}</td>
                    <td>{payment.mobile_number || '-'}</td>
                    <td>
                      <ProofCell url={payment.proof_image_url} />
                    </td>
                    <td>{payment.agent || '-'}</td>
                    <td>{formatDateTime(payment.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-small"
                        disabled={deletingId === payment.id}
                        onClick={() => handleDelete(payment)}
                      >
                        <TrashIcon width={14} height={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredPayments.length}
      />

      {showAddForm && <AddBackendPaymentModal onClose={() => setShowAddForm(false)} onSubmit={handleAdd} />}
    </>
  );
}

export default BackendPayments;
